import { useTenantQuery } from '@/modules/dashboard/hooks/use-tenant-query';

/**
 * Modules the TENANT switches on themselves, on top of what their plan and
 * their Role Matrix grant allow.
 *
 * This is a third, independent axis of visibility:
 *   - plan        → do they pay for it            (`plan-gates.ts`)
 *   - permission  → is their role allowed it      (Role Matrix)
 *   - activation  → have they turned it ON        (this file)
 *
 * Unlike the first two, a failed activation check is NOT an upsell and NOT an
 * access denial — the feature simply isn't set up yet, so the module is hidden
 * rather than locked. Today only Website Analytics works this way.
 */
export type ActivationKey = 'web_analytics';

export interface ModuleActivation {
    /** The module is switched on and its dashboard should be reachable. */
    isActive:  boolean;
    /** The answer isn't known yet — route guards must WAIT, not redirect. */
    isLoading: boolean;
}

/**
 * Website Analytics is live only once the tenant clicks "Activate tracker" in
 * Settings → Integrations. Pausing it hides the module again.
 *
 * Resolution rules, in order:
 *   - still loading      → inactive, so a hidden module never flashes into the
 *                          sidebar and back out. Route guards check `isLoading`
 *                          first and hold instead of bouncing a direct URL.
 *   - tenant unreadable  → active. `useTenantQuery` swallows API errors into
 *                          `null`, and gating must never hard-block on missing
 *                          data (same rule as `planAllows`/`hasIntegration`).
 *                          The page's own empty state still tells the truth.
 *   - otherwise          → the stored flag.
 */
export function useWebAnalyticsActivation(): ModuleActivation {
    // `isLoading` (not `isPending`) so the platform-admin case — where the
    // query is disabled and therefore pends forever — resolves permissively
    // instead of hanging a route guard on a blank screen.
    const { data: tenant, isLoading } = useTenantQuery();
    return {
        isActive:  tenant ? tenant.web_analytics_enabled === true : !isLoading,
        isLoading,
    };
}
